import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiService } from "../../../services/apiService";
import analyticsService from "../../../services/analyticsService";
import rc_yes from "../../assets/rc-yes.svg";
import rc_no from "../../assets/rc-no.svg";
import "../../components/ModelOverlay/RequestGatepass.css";
import { Button, ImageWithFallback } from "../../components/Utilities";
import pro from "../../assets/person.svg";
import loc from "../../assets/locCard.svg";
import { Loading } from "../Loading";

export const GatePassModal = ({
  isOpen,
  onClose,
  yard,
  onChooseEvaluator,
  onAddEvaluator,
  cars,
  onDriverAdded,
  onGatePassCreated,
  yardMap = null,
  buildCleanMapIframe,
  getMapUrl,
}) => {
  const navigate = useNavigate();
  const [selectedEvaluator, setSelectedEvaluator] = useState("");
  const [selectedCars, setSelectedCars] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [profilePicture, setProfilePicture] = useState(null);
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [driverError, setDriverError] = useState(null);
  const [gatePassError, setGatePassError] = useState(null);
  const [loadingGatePass, setLoadingGatePass] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);

  // Initialize selectedCars with all car IDs when modal opens
  useEffect(() => {
    if (isOpen && cars.length > 0) {
      setSelectedCars(cars.map((car) => car.id));
    } else {
      setSelectedCars([]);
    }
  }, [isOpen, cars]);

  // Fetch drivers when modal opens and set default evaluator
  useEffect(() => {
    if (isOpen) {
      const fetchDrivers = async () => {
        try {
          setLoadingDrivers(true);
          const response = await apiService.getDrivers("evaluator");
          console.log("Fetched drivers:", response.data);
          const fetchedDrivers = response.data || [];
          setDrivers(fetchedDrivers);
          setDriverError(null);

          // Set the first evaluator as default if available
          if (fetchedDrivers.length > 0) {
            setSelectedEvaluator(fetchedDrivers[0].name);
            onChooseEvaluator();
          } else {
            setSelectedEvaluator(""); // Keep "Choose Evaluator" if no drivers
          }
        } catch (err) {
          console.error("Error fetching drivers:", err);
          setDriverError("Failed to load drivers. Please try again.");
          setDrivers([]);
          setSelectedEvaluator(""); // Default to "Choose Evaluator" on error
        } finally {
          setLoadingDrivers(false);
        }
      };
      fetchDrivers();
    }
  }, [isOpen, onChooseEvaluator]);

  // Refresh drivers when a new driver is added and set the new driver as default
  useEffect(() => {
    if (onDriverAdded) {
      const fetchDrivers = async () => {
        try {
          setLoadingDrivers(true);
          const response = await apiService.getDrivers("evaluator");
          console.log("Refreshed drivers:", response.data);
          const fetchedDrivers = response.data || [];
          setDrivers(fetchedDrivers);
          setDriverError(null);

          // Set the first evaluator (newly added) as default if available
          if (fetchedDrivers.length > 0) {
            setSelectedEvaluator(fetchedDrivers[0].name);
            onChooseEvaluator();
          } else {
            setSelectedEvaluator(""); // Keep "Choose Evaluator" if no drivers
          }
        } catch (err) {
          console.error("Error fetching drivers:", err);
          setDriverError("Failed to load drivers. Please try again.");
          setDrivers([]);
          setSelectedEvaluator(""); // Default to "Choose Evaluator" on error
        } finally {
          setLoadingDrivers(false);
        }
      };
      fetchDrivers();
    }
  }, [onDriverAdded, onChooseEvaluator]);

  // Update profilePicture when selectedEvaluator changes
  useEffect(() => {
    if (selectedEvaluator && selectedEvaluator !== "add-evaluator") {
      const selectedDriver = drivers.find(
        (driver) => driver.name === selectedEvaluator
      );
      if (selectedDriver && selectedDriver.imageUrl) {
        console.log({ drivers });
        setProfilePicture(selectedDriver.imageUrl);
      } else {
        setProfilePicture(null);
      }
    } else {
      setProfilePicture(null);
    }
  }, [selectedEvaluator, drivers]);

  if (!isOpen) return null;

  const handleEvaluatorChange = (e) => {
    const value = e.target.value;
    setSelectedEvaluator(value);
    if (value === "add-evaluator") {
      onAddEvaluator();
      setSelectedEvaluator(""); // Temporarily reset until new driver is added
    } else {
      onChooseEvaluator();
    }
  };

  const handleCarCheckboxChange = (carId) => {
    setSelectedCars((prev) =>
      prev.includes(carId)
        ? prev.filter((id) => id !== carId)
        : [...prev, carId]
    );
  };

  const handleRequestGatePass = async () => {
    if (!selectedEvaluator || selectedCars.length === 0) {
      console.warn("Missing evaluator or cars:", {
        selectedEvaluator,
        selectedCars,
      });
      setGatePassError("Please select an evaluator and at least one car.");
      return;
    }

    setLoadingGatePass(true);
    setLoading(true);
    setGatePassError(null);

    try {
      const selectedDriver = drivers.find(
        (driver) => driver.name === selectedEvaluator
      );
      if (!selectedDriver) {
        console.error("Selected evaluator not found:", selectedEvaluator);
        setGatePassError("Selected evaluator not found.");
        setLoadingGatePass(false);
        setLoading(false);
        return;
      }

      const selectedCarObjects = cars.filter((car) =>
        selectedCars.includes(car.id)
      );
      console.log(
        "Selected cars:",
        selectedCarObjects.map((car) => ({
          id: car.id,
          car_id: car.car_id,
          auctionId: car.auctionId,
        }))
      );

      // Log full car object structure for debugging
      if (selectedCarObjects.length > 0) {
        console.log("🚗 Full car object structure:", selectedCarObjects[0]);
        console.log(
          "🚗 All car object keys:",
          Object.keys(selectedCarObjects[0])
        );
        console.log(
          "🚗 Car object values:",
          Object.values(selectedCarObjects[0])
        );

        // Check for any field that might contain registration info
        const car = selectedCarObjects[0];
        console.log("🚗 Searching for registration in all fields:");
        Object.keys(car).forEach((key) => {
          const value = car[key];
          if (typeof value === "string" && value.length > 0) {
            console.log(`🚗 Field "${key}": "${value}"`);
          }
        });

        // Check if there's a nested object with registration info
        console.log("🚗 Checking for nested objects:");
        Object.keys(car).forEach((key) => {
          const value = car[key];
          if (typeof value === "object" && value !== null) {
            console.log(`🚗 Nested object "${key}":`, value);
            if (typeof value === "object") {
              Object.keys(value).forEach((nestedKey) => {
                console.log(
                  `🚗   Nested field "${nestedKey}": "${value[nestedKey]}"`
                );
              });
            }
          }
        });
      }

      // Filter out cars with existing gatepass requests or approved gate passes
      const validCars = selectedCarObjects.filter(
        (car) =>
          car.state !== "GATE_PASS_REQUESTED" &&
          car.state !== "GATE_PASS_APPROVED"
      );
      if (validCars.length === 0) {
        console.warn(
          "No valid cars to request gatepass: all cars have pending or approved gatepass requests"
        );
        setGatePassError(
          "All selected cars already have pending or approved gatepass requests."
        );
        setLoadingGatePass(false);
        setLoading(false);
        return;
      }

      // Group cars by auctionId
      const carsByAuction = validCars.reduce((acc, car) => {
        const auctionId = car.auctionId;
        if (!auctionId) {
          console.warn(`Car ${car.id} is missing auctionId`);
          return acc;
        }
        if (!car.car_id) {
          console.warn(`Car ${car.id} is missing car_id`);
          return acc;
        }
        if (!acc[auctionId]) {
          acc[auctionId] = [];
        }
        acc[auctionId].push(car.car_id);
        return acc;
      }, {});

      console.log("Cars grouped by auctionId:", carsByAuction);

      if (Object.keys(carsByAuction).length === 0) {
        console.warn(
          "No valid cars with auctionId and car_id to request gatepass"
        );
        setGatePassError(
          "No valid cars with auction ID and car ID to request gatepass."
        );
        setLoadingGatePass(false);
        setLoading(false);
        return;
      }

      // Make gatepass requests for each auctionId
      const gatePassResponses = [];
      for (const [auctionId, carIds] of Object.entries(carsByAuction)) {
        const gatePassData = {
          auctionId,
          carIds,
          driverId: selectedDriver.id,
          dealerId: "adminuid",
        };

        console.log(
          "Sending gate pass data for auction:",
          auctionId,
          gatePassData
        );
        const response = await apiService.createGatepass(gatePassData);
        console.log(
          `Gate pass created successfully for auction ${auctionId}:`,
          response
        );
        if (response.status === 200) {
          gatePassResponses.push({
            auctionId,
            carIds,
            gatepassId: response.data.gatepassId,
          });
        } else {
          throw new Error(
            `Failed to create gate pass for auction ${auctionId}: Status ${response.status}`
          );
        }
      }

      // Track analytics for each car in the gate pass request
      selectedCarObjects.forEach((car) => {
        console.log("🚗 Dealer RequestGatepass - Raw car object:", car);
        console.log(
          "🚗 Dealer RequestGatepass - Available car fields:",
          Object.keys(car)
        );
        console.log(
          "🚗 Dealer RequestGatepass - Car brand/make:",
          car.brand,
          car.make
        );
        console.log("🚗 Dealer RequestGatepass - Car model:", car.model);
        console.log("🚗 Dealer RequestGatepass - Car year:", car.year);
        console.log("🚗 Dealer RequestGatepass - Car regno:", car.regno);
        console.log(
          "🚗 Dealer RequestGatepass - Car registrationNumber:",
          car.registrationNumber
        );
        console.log("🚗 Dealer RequestGatepass - Car regNo:", car.regNo);
        console.log(
          "🚗 Dealer RequestGatepass - Car registration:",
          car.registration
        );
        console.log("🚗 Dealer RequestGatepass - Car reg_no:", car.reg_no);
        console.log(
          "🚗 Dealer RequestGatepass - Car regNumber:",
          car.regNumber
        );
        console.log(
          "🚗 Dealer RequestGatepass - Car vehicleNumber:",
          car.vehicleNumber
        );
        console.log(
          "🚗 Dealer RequestGatepass - Car numberPlate:",
          car.numberPlate
        );
        console.log(
          "🚗 Dealer RequestGatepass - Car plateNumber:",
          car.plateNumber
        );
        console.log("🚗 Dealer RequestGatepass - Car city:", car.city);
        console.log("🚗 Dealer RequestGatepass - Car location:", car.location);
        console.log(
          "🚗 Dealer RequestGatepass - Car supplierName:",
          car.supplierName
        );
        console.log("🚗 Dealer RequestGatepass - Car supplier:", car.supplier);
        console.log("🚗 Dealer RequestGatepass - Car car_id:", car.car_id);
        console.log("🚗 Dealer RequestGatepass - Car id:", car.id);

        // Try to find registration number in any field
        let registrationNumber = "N/A";
        const possibleRegFields = [
          "rto",
          "regno",
          "registrationNumber",
          "regNo",
          "registration",
          "reg_no",
          "regNumber",
          "vehicleNumber",
          "numberPlate",
          "plateNumber",
          "vehicle_reg_no",
          "vehicle_reg",
          "reg_number",
          "plate_number",
          "vehicle_no",
          "vehicle_number",
          "car_number",
          "car_no",
        ];

        for (const field of possibleRegFields) {
          if (
            car[field] &&
            typeof car[field] === "string" &&
            car[field].trim().length > 0
          ) {
            registrationNumber = car[field].trim();
            console.log(
              `🚗 Found registration in field "${field}": "${registrationNumber}"`
            );
            break;
          }
        }

        // If not found in direct fields, check nested objects
        if (registrationNumber === "N/A") {
          Object.keys(car).forEach((key) => {
            const value = car[key];
            if (typeof value === "object" && value !== null) {
              for (const field of possibleRegFields) {
                if (
                  value[field] &&
                  typeof value[field] === "string" &&
                  value[field].trim().length > 0
                ) {
                  registrationNumber = value[field].trim();
                  console.log(
                    `🚗 Found registration in nested field "${key}.${field}": "${registrationNumber}"`
                  );
                  break;
                }
              }
            }
          });
        }

        console.log(`🚗 Final registration number: "${registrationNumber}"`);

        const analyticsData = {
          carMake: car.brand || car.make || "Unknown",
          carModel: car.model || "Unknown",
          carYear: car.year || "Unknown",
          dealerName:
            localStorage.getItem("name") ||
            localStorage.getItem("email") ||
            "-",
          supplierName: car.supplierName || car.supplier || "Unknown",
          carId: car.car_id || car.id || "Unknown",
          registrationNumber: registrationNumber,
          price: car.price || 0,
          location: car.city || car.location || "Unknown",
          yardName: yard?.name || "Unknown",
          gatePassType: "Standard",
        };

        console.log(
          "🚗 Dealer RequestGatepass - Analytics data being sent:",
          analyticsData
        );
        analyticsService.trackGatePassRequested(analyticsData);
      });

      // Update state to move all selected cars to GATE_PASS_REQUESTED
      onGatePassCreated(selectedCars);
      onClose();
    } catch (err) {
      console.error("Error creating gate pass:", err);
      let errorMessage = "An error occurred while creating the gate pass.";
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.code === "ECONNABORTED") {
        errorMessage = "Request timed out. Please check your network.";
      } else if (err.response?.status === 400) {
        errorMessage = `Invalid data provided: ${
          err.response.data?.error || "Please check your inputs."
        }`;
      } else if (err.response?.status === 401) {
        errorMessage = "Unauthorized. Please log in again.";
        navigate("/");
      } else if (err.response?.status === 500) {
        errorMessage = `Server error: ${
          err.response.data?.error || "Please try again later."
        }`;
      } else {
        errorMessage = `Failed to create gate pass: ${err.message}`;
      }
      console.error("Error message displayed to user:", errorMessage);
      setGatePassError(errorMessage);
    } finally {
      setLoadingGatePass(false);
      setLoading(false);
    }
  };

  const isButtonActive =
    selectedEvaluator &&
    selectedEvaluator !== "add-evaluator" &&
    selectedCars.length > 0;

  // Handler to open image modal
  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setIsImageModalOpen(true);
  };

  // Handler to close image modal
  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  return (
    <div className="request-gate-pass-modal">
      <div className="modal-overlay">
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="model-header">
            <p>Request Gatepass</p>
            <button className="modal-close" onClick={onClose}>
              ×
            </button>
          </div>

          <div className="model-body">
            <div className="right-part">
              <div className="evaluator-section">
                {profilePicture == null ? (
                  <div className="profile-pic">
                    <img src={pro} alt="" width="50%" />
                  </div>
                ) : (
                  <div className="img-view">
                    <div className="profile-pic">
                      <img
                        src={profilePicture}
                        alt=""
                        width="50%"
                        className="uploadModel-img"
                        onClick={() => handleImageClick(profilePicture)}
                      />
                    </div>
                    <div>
                      {selectedEvaluator &&
                        selectedEvaluator !== "add-evaluator" && (
                          <p className="mobile-selected-evaluator">
                            For{" "}
                            <div className="spanElement">
                              {selectedEvaluator}
                            </div>
                          </p>
                        )}
                    </div>
                  </div>
                )}

                <div className="evaluator-section-part">
                  {selectedEvaluator &&
                    selectedEvaluator !== "add-evaluator" && (
                      <p className="selected-evaluator">
                        For{" "}
                        <div className="spanElement">{selectedEvaluator}</div>
                      </p>
                    )}
                  {loadingDrivers ? (
                    <p>Loading evaluators...</p>
                  ) : driverError ? (
                    <p className="error">{driverError}</p>
                  ) : (
                    <div className="emu-selector">
                      <select
                        value={selectedEvaluator}
                        onChange={handleEvaluatorChange}
                        className="evaluator-select"
                      >
                        <option value="">Choose Evaluator</option>
                        {drivers.map((driver) => (
                          <option key={driver.id} value={driver.name}>
                            {driver.name}
                          </option>
                        ))}
                        <option value="add-evaluator">Add an Evaluator</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
              <div className="map-container">
                <div
                  className="map-wrapper"
                  onClick={() => {
                    if (yardMap) window.open(getMapUrl(yardMap), "_blank");
                  }}
                  style={{ cursor: yardMap ? "pointer" : "default" }}
                >
                  <div
                    className="map-iframe"
                    style={{ pointerEvents: "none" }}
                    dangerouslySetInnerHTML={{
                      __html: yardMap
                        ? buildCleanMapIframe(yardMap)
                        : '<div style="background:#f0f0f0; display:flex; align-items:center; justify-content:center; color:#999; font-size:14px; border-radius:8px;">Map not available</div>',
                    }}
                  />
                </div>
                <div className="yard-name-wrapper">
                  <img src={loc} alt="" className="loc-card"/>
                  <p className="yard-name">{yard || "Unknown"}</p>
                </div>
              </div>
            </div>

            <div className="left-part">
              <div className="cars-list">
                {cars && cars.length > 0 ? (
                  cars.map((car) => (
                    <div key={car.id} className="car-card modal-car-card">
                      <input
                        type="checkbox"
                        checked={selectedCars.includes(car.id)}
                        onChange={() => handleCarCheckboxChange(car.id)}
                        className="car-checkbox"
                      />
                      <div className="car-image-wrapper">
                        <ImageWithFallback
                          src={car.image}
                          alt={`${car.brand} ${car.model}`}
                          className="car-image"
                          onClick={() => handleImageClick(car.image)}
                        />
                      </div>
                      <div className="car-details">
                        <h3 className="car-title">
                          {car.year} {car.brand} {car.model}
                        </h3>
                        <div className="car-specs">
                          {car.km.toLocaleString()} kms • {car.fuel} •{" "}
                          {car.transmission}
                        </div>
                        <div className="car-tags">
                          {car.rc ? (
                            <img src={rc_yes} alt="RC Yes" className="rc" />
                          ) : (
                            <img src={rc_no} alt="RC No" className="rc" />
                          )}
                          <span className="rto-tag">{car.rto}</span>
                          <span className="owner-tag">{car.owner}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No cars available in this yard.</p>
                )}
              </div>
              {gatePassError && <p className="error">{gatePassError}</p>}
              <Button
                variant="primary"
                disabled={!isButtonActive || loadingGatePass}
                className="request-gate-pass-btn"
                onClick={handleRequestGatePass}
              >
                {loadingGatePass ? "Requesting..." : "Request Gate Pass"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {isImageModalOpen && selectedImage && (
        <div className="image-modal-overlay" onClick={handleCloseImageModal}>
          <div
            className="image-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="image-modal-close"
              onClick={handleCloseImageModal}
            >
              ×
            </button>
            <img
              src={selectedImage}
              alt="Full-size Preview"
              className="full-size-image"
            />
          </div>
        </div>
      )}
      {loading && <Loading />}
    </div>
  );
};
